import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, TextField, Typography, Alert, Stack, Link, useTheme } from "@mui/material";
import { useAuth } from "./AuthContext.js";
import { useColorMode } from "../theme/ColorModeContext.js";
import { GlowButton } from "../components/GlowButton.js";

type Mode = "login" | "signup" | "verify" | "forgot" | "reset";

export function LoginPage() {
  const { login, signup, confirmSignUp, forgotPassword, confirmForgotPassword, error } = useAuth();
  const nav = useNavigate();
  const theme = useTheme();
  const { mode: colorMode } = useColorMode();

  const [mode, setMode]             = useState<Mode>("login");
  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [confirmPw, setConfirmPw]   = useState("");
  const [code, setCode]             = useState("");
  const [loading, setLoading]       = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [info, setInfo]             = useState<string | null>(null);

  const displayError = localError ?? error;

  const switchMode = (next: Mode) => {
    setLocalError(null);
    setInfo(null);
    setCode("");
    setConfirmPw("");
    setMode(next);
  };

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8)        return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(pw))    return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(pw))    return "Password must contain at least one lowercase letter";
    if (!/[0-9]/.test(pw))    return "Password must contain at least one number";
    return null;
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setLoading(true);
    try {
      await forgotPassword(email);
      setMode("reset");
      setCode("");
      setConfirmPw("");
      setInfo(`We sent a reset code to ${email}. Enter it below with your new password.`);
    } catch {
      // error shown via context
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password !== confirmPw) { setLocalError("Passwords do not match"); return; }
    const pwErr = validatePassword(password);
    if (pwErr) { setLocalError(pwErr); return; }
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, password);
      await login(email, password);
      nav("/dashboard");
    } catch {
      // error shown via context
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch {
      // error shown via context
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password !== confirmPw) { setLocalError("Passwords do not match"); return; }
    const pwErr = validatePassword(password);
    if (pwErr) { setLocalError(pwErr); return; }
    setLoading(true);
    try {
      await signup(email, password);
      switchMode("verify");
    } catch {
      // error shown via context
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setLoading(true);
    try {
      await confirmSignUp(email, code);
      // Auto-login after verification
      await login(email, password);
      nav("/dashboard");
    } catch {
      // error shown via context
    } finally {
      setLoading(false);
    }
  };

  const headings: Record<Mode, string> = {
    login:  "Sign in",
    signup: "Create account",
    verify: "Check your email",
    forgot: "Reset your password",
    reset:  "Set a new password",
  };

  const subheadings: Record<Mode, string> = {
    login:  "Build AI chatbots from your documents.",
    signup: "Get started for free — no credit card required.",
    verify: `We sent a verification code to ${email}.`,
    forgot: "Enter your email and we'll send you a reset code.",
    reset:  `We sent a code to ${email}.`,
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      position: "relative",
      overflow: "hidden",
      bgcolor: "background.default",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: 2,
    }}>
      {/* Brand gradient overlay — cyan top-left → yellow bottom-right at 25% opacity */}
      <Box sx={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(135deg, rgba(51,197,232,0.25) 0%, rgba(51,197,232,0.05) 45%, rgba(245,213,71,0.20) 100%)",
        pointerEvents: "none",
        zIndex: 0,
      }} />
      <Box sx={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: 400,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "6px",
        p: "40px",
        boxShadow: theme.shadows[1],
      }}>
        <Stack spacing={3}>
          {/* Logo */}
          <Box>
            <Box sx={{ mb: 2.5, textAlign: "center" }}>
              <img
                src={colorMode === "dark" ? "/chatfab-logo-dark.png" : "/chatfab-logo.png"}
                alt="ChatFab"
                style={{ height: 32 }}
              />
            </Box>
            <Typography variant="h2" gutterBottom sx={{ fontFamily: "'Outfit', sans-serif" }}>
              {headings[mode]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subheadings[mode]}
            </Typography>
          </Box>

          {displayError && <Alert severity="error">{displayError}</Alert>}
          {info && !displayError && <Alert severity="info">{info}</Alert>}

          {/* ── Login ── */}
          {mode === "login" && (
            <form onSubmit={handleLogin}>
              <Stack spacing={2}>
                <TextField
                  label="Email" type="email" fullWidth autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)} required
                />
                <TextField
                  label="Password" type="password" fullWidth autoComplete="current-password"
                  value={password} onChange={e => setPassword(e.target.value)} required
                />
                <Stack direction="row" justifyContent="flex-end">
                  <Link component="button" type="button" onClick={() => switchMode("forgot")} underline="hover" variant="body2">
                    Forgot password?
                  </Link>
                </Stack>
                <GlowButton type="submit" loading={loading} size="lg">
                  {loading ? "Signing in…" : "Sign in"}
                </GlowButton>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Don't have an account?{" "}
                  <Link component="button" type="button" onClick={() => switchMode("signup")} underline="hover">
                    Sign up
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}

          {/* ── Forgot password ── */}
          {mode === "forgot" && (
            <form onSubmit={handleForgot}>
              <Stack spacing={2}>
                <TextField
                  label="Email" type="email" fullWidth autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)} required
                />
                <GlowButton type="submit" loading={loading} size="lg">
                  {loading ? "Sending…" : "Send reset code"}
                </GlowButton>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  <Link component="button" type="button" onClick={() => switchMode("login")} underline="hover">
                    Back to sign in
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}

          {/* ── Reset password ── */}
          {mode === "reset" && (
            <form onSubmit={handleReset}>
              <Stack spacing={2}>
                <TextField
                  label="Verification code" type="text" fullWidth autoComplete="one-time-code"
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  value={code} onChange={e => setCode(e.target.value)} required
                />
                <TextField
                  label="New password" type="password" fullWidth autoComplete="new-password"
                  value={password} onChange={e => setPassword(e.target.value)} required
                />
                <TextField
                  label="Confirm new password" type="password" fullWidth autoComplete="new-password"
                  value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required
                />
                <GlowButton type="submit" loading={loading} size="lg">
                  {loading ? "Updating…" : "Set new password"}
                </GlowButton>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  <Link component="button" type="button" onClick={() => switchMode("login")} underline="hover">
                    Back to sign in
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}

          {/* ── Sign up ── */}
          {mode === "signup" && (
            <form onSubmit={handleSignup}>
              <Stack spacing={2}>
                <TextField
                  label="Email" type="email" fullWidth autoComplete="email"
                  value={email} onChange={e => setEmail(e.target.value)} required
                />
                <TextField
                  label="Password" type="password" fullWidth autoComplete="new-password"
                  value={password} onChange={e => setPassword(e.target.value)} required
                  helperText=" "
                />
                <TextField
                  label="Confirm password" type="password" fullWidth autoComplete="new-password"
                  value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required
                />
                <GlowButton type="submit" loading={loading} size="lg">
                  {loading ? "Creating account…" : "Create account"}
                </GlowButton>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Already have an account?{" "}
                  <Link component="button" type="button" onClick={() => switchMode("login")} underline="hover">
                    Sign in
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}

          {/* ── Verify ── */}
          {mode === "verify" && (
            <form onSubmit={handleVerify}>
              <Stack spacing={2}>
                <TextField
                  label="Verification code" type="text" fullWidth autoComplete="one-time-code"
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  value={code} onChange={e => setCode(e.target.value)} required
                />
                <GlowButton type="submit" loading={loading} size="lg">
                  {loading ? "Verifying…" : "Verify email"}
                </GlowButton>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Wrong email?{" "}
                  <Link component="button" type="button" onClick={() => switchMode("signup")} underline="hover">
                    Go back
                  </Link>
                </Typography>
              </Stack>
            </form>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
